import React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Chip from '@mui/material/Chip';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import './MultiSelectDropdown.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const MultiSelectDropdown = ({
  optionList = [],
  value = [],
  limit = 3,
  onUpdateFilter,
  filterKey,
  defaultFilterValue,
  label,
  error,
  helperText,
  placeholder = '',
  openOnFocus = true,
  setOpen = () => {},
  loading,
  ...rest
}) => {
  const options = optionList ?? [];

  const handleOptionSelect = (event, value) => {
    event.preventDefault();
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div style={{ position: 'relative' }}>
        <Autocomplete
          className='custom-autocomplete__multi'
          multiple
          limitTags={limit}
          id={`multiple-limit-tags-${filterKey}`}
          options={options}
          getOptionLabel={(option) => option.label}
          renderTags={() => null} // Disable the default tag rendering
          renderOption={(props, option) => {
            const isSelected = value?.some((v) => v.value === option.value);
            const { key, ...optionProps } = props;
            return (
              <li key={`multiple-limit-tags-${filterKey}-${key}-${option.value}`} {...optionProps}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={isSelected}
                />
                {option.label}
              </li>
            );
          }}
          value={value}
          onOpen={() => setOpen(true)}
          openOnFocus={openOnFocus}
          renderInput={(params) => {
            const filteredValue = value?.filter((v) => v.id !== 'all');
            const visibleChips = filteredValue?.slice(0, limit);
            const extraCount = filteredValue?.length > limit ? `+${filteredValue?.length - limit}` : '';

            return (
              <TextField
                {...params}
                label={label}
                error={error}
                helperText={helperText}
                variant='standard'
                placeholder={placeholder}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleOptionSelect(event, params.inputValue);
                  }

                  if (event.key === 'Escape') {
                    setOpen(false);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  style: { display: 'flex', alignItems: 'center', flexWrap: 'nowrap' },
                  startAdornment: (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                      }}
                    >
                      {visibleChips.map((option) => (
                        <Chip
                          key={option.value}
                          label={option.label}
                          size='small'
                          style={{ marginRight: 4 }}
                        />
                      ))}
                      {extraCount && <Chip label={extraCount} size='small' style={{ marginRight: 4 }} />}
                    </div>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      <div className='m-r-20 box-end'>
                        {loading ? (
                          <FontAwesomeIcon className='text-muted' icon='circle-notch' spin />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </div>
                    </React.Fragment>
                  ),
                  size: 'small',
                }}
              />
            );
          }}
          onChange={(event, newInputValue) => {
            const optionIndex = event.currentTarget.dataset.optionIndex;
            const element = optionList[optionIndex];
            const existingIndex = value.findIndex((v) => v.id === element?.id);

            let newOptions = [];
            if (existingIndex !== -1) {
              newOptions = value?.filter((v) => v.id !== element?.id)?.map((year) => year?.id);
            } else {
              newOptions = newInputValue?.map((year) => year?.id);
            }

            onUpdateFilter({ ...defaultFilterValue, [filterKey]: newOptions });
          }}
          {...rest}
        />
      </div>
    </ClickAwayListener>
  );
};

export default MultiSelectDropdown;
